import React, { FC, useCallback, useEffect, useMemo } from 'react';

import { Box, Typography } from '@mui/material';

import {
  HelpTypes,
  adminActions,
  adminPharmacistPictures,
  PermissionsOfAdmin,
  TimeFormat,
} from '@pharmaplan/common';
import { ReactComponent as StarFilled } from '@pharmaplan/common/assets/icons/starFilled.svg';

import ProfileHeader from '../../../ProfileHeader';
import {
  Constants,
  OtherScreens,
  ScreenTypes,
  SecondaryAdminNavigation,
  SkeletonVariants,
} from '../../../../../../helpers/Constants';
import TakeOwnership from '../../../../PendingActivationList/TakeOwnership';
import strings from '../../../../../../localization';
import usePharmacistProfileViewDetails from '../../../../../../hooks/Admin/Profile/usePharmacistViewDetails';
import WithSkeleton from '../../../../../common/WithSkeleton';
import CustomButton from '../../../../../common/CustomButton';
import { useAppDispatch } from '../../../../../../hooks/useAppDispatch';
import {
  ICustomBack,
  renderScreen,
} from '../../../../../../actions/drawerActions';
import useGetItems from '../../../../../../hooks/useGetItems';
import useAdminPermissions from '../../../../../../hooks/Admin/useAdminPermissions';

import { IPharmacistViewProfile } from '../PharmacistViewProfile';
import classes from './style';
import useDynamicProperties from '../../../../../../hooks/useDynamicProperties';
import usePharmacistList from '../../../../../../hooks/PharmacistList/usePharmacistList';
import CrossPlatformProfileStatus from '../../../../../common/CrossPlatformProfileStatus';
import { getDayMonthDateYear, getHourMinute } from '../../../../../Reports/helpers';

const { text } = Constants.variant;

interface IAdminPharmacistCard extends IPharmacistViewProfile {
  ratingGoBack?: ICustomBack;
  showActions?: boolean;
  isExternal?: boolean;
}

const { pendingActivation } = SecondaryAdminNavigation;
const { DeletedPendingUsers } = OtherScreens;
const { bookingList } = ScreenTypes;

const AdminPharmacistCard: FC<IAdminPharmacistCard> = ({
  fromScreen,
  pharmacistId,
  type,
  hasOwner,
  showActions = false,
  showMenu,
  ratingGoBack,
  isExternal,
}) => {
  const dispatch = useAppDispatch();
  const { can } = useAdminPermissions();

  const { professionalUser, id } = useDynamicProperties();
  const canActivate = can(PermissionsOfAdmin.Activation);

  const {
    email,
    firstName,
    lastName,
    code,
    rating,
    userType = HelpTypes.pharmacist,
    portedToPP = false,
    portedFromPG = false,
    canSendInvite = false,
    canResumePorting = false,
    lastPPInviteSentAt = null,
    timeFormat = TimeFormat.twentyFourHours,
  } = professionalUser ?? {};
  const { totalRating } = rating ?? {};

  const headerTitle = `${firstName} ${lastName}`;

  const fromPendingActivations = fromScreen === pendingActivation;
  const isDeletedPendingUser = fromScreen === DeletedPendingUsers;
  const isDeletedPharmacistList = fromScreen === OtherScreens.DeletedPharmacistList;

  const noActionsScreens = [
    DeletedPendingUsers,
    OtherScreens.PharmacyList,
    OtherScreens.incompatibleMatches,
    OtherScreens.AdminReports,
    OtherScreens.ListOfWorkshifts,
    OtherScreens.BroadcastGroups,
    OtherScreens.DeletedPendingUsers,
    OtherScreens.DeletedPharmacistList,
  ];

  const noActionsScreen = noActionsScreens.includes(fromScreen as OtherScreens);

  const isDeletedList = isDeletedPendingUser || isDeletedPharmacistList;
  const isFromMap = fromScreen === OtherScreens.Map;

  const pId = isDeletedList ? id : pharmacistId;

  const successAction = isDeletedList
    ? adminActions.deletedUserProfile
    : adminActions.getPharmacistDetails;

  const SkeletonTypography = WithSkeleton({
    successAction,
    variant: SkeletonVariants.text,
  })(Typography);

  const calcRating = ((totalRating ?? 0) * 5) / 100;

  const viewPharmacistProfile = useCallback(() => {
    dispatch(
      renderScreen({
        screenType: ScreenTypes.profile,
        screenNumber: 2,
        pharmacistId,
        fromScreen,
      }),
    );
  }, [dispatch, pharmacistId, fromScreen]);

  const viewAdminRating = () => {
    dispatch(
      renderScreen({
        screenNumber: 3,
        screenType: bookingList,
        pharmacistId: id,
        type,
        back: ratingGoBack ?? viewPharmacistProfile,
      }),
    );
  };

  const Rating = useCallback(
    () =>
      (
        <SkeletonTypography sx={classes.ratingContainer}>
          <StarFilled width={11} height={11} />
          <SkeletonTypography sx={classes.ratingText}>
            {calcRating.toPrecision(2)}
          </SkeletonTypography>

          <CustomButton
            customButtonStyle={classes.customLink}
            label={`(${strings.viewRating})`}
            onClick={viewAdminRating}
            variant={text}
          />
        </SkeletonTypography>
      ),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [calcRating, viewAdminRating],
  );

  const config = [
    {
      key: '0',
      value: <Typography sx={classes.codeFont}>{code}</Typography>,
    },
  ];

  const invitationTimestamp = lastPPInviteSentAt
    ? [
      getDayMonthDateYear(lastPPInviteSentAt),
      getHourMinute(lastPPInviteSentAt, timeFormat as TimeFormat),
    ].join(' | ')
    : null;

  if (invitationTimestamp) {
    config.push({
      key: 'crossPlatformInvitation',
      value: (
        <Typography sx={classes.codeFont}>
          {strings.formatString(
            strings.lastPharmaPlanInvitationSent,
            invitationTimestamp,
          )}
        </Typography>
      ),
    });
  }

  if (!isExternal) {
    config.push({
      key: '1',
      value: <Rating />,
    });
  }

  const { actions } = usePharmacistProfileViewDetails(
    userType,
    fromScreen,
    hasOwner,
    pharmacistId,
    canSendInvite,
    canResumePorting,
    lastPPInviteSentAt,
  );

  const { actions: listActions } = usePharmacistList(
    userType ?? HelpTypes.pharmacist,
  );

  const profileCentricActions = useMemo(() => {
    const backToPharmacistProfile = isFromMap ? viewPharmacistProfile : ratingGoBack;

    if (isFromMap) {
      return [
        ...actions(pharmacistId, type, backToPharmacistProfile),
        ...listActions({
          pharmacistId,
          pharmacistName: headerTitle,
          back: backToPharmacistProfile,
        }).slice(
          0,
          -3,
        ),
      ];
    }
    return actions(pharmacistId, type, backToPharmacistProfile);
  }, [
    isFromMap,
    actions,
    pharmacistId,
    type,
    ratingGoBack,
    listActions,
    headerTitle,
    viewPharmacistProfile,
  ]);

  const { getPharmacistImage } = useGetItems();

  const image = getPharmacistImage(pId);

  useEffect(() => {
    if (pId) {
      dispatch(adminPharmacistPictures([pId]));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pId]);

  return (
    <ProfileHeader
      successAction={successAction}
      avatar={image ?? ''}
      config={config}
      title={headerTitle}
      menuActions={
        noActionsScreen || !showActions ? null : profileCentricActions
      }
      showMenu={showMenu}
      customFooter={(
        <Box sx={classes.footerContainer}>
          <Typography sx={classes.contactNameTitle}>{strings.email}</Typography>

          <SkeletonTypography sx={classes.contactName}>
            {email}
          </SkeletonTypography>

          {fromPendingActivations && !hasOwner && canActivate && (
            <TakeOwnership id={pharmacistId} userType={HelpTypes.pharmacist} />
          )}
        </Box>
      )}
      renderChip={(
        <CrossPlatformProfileStatus
          portedToPP={portedToPP}
          portedFromPG={portedFromPG}
        />
      )}
    />
  );
};

export default AdminPharmacistCard;
